import * as Sentry from '@sentry/browser';
import { useAuth } from '../services/firebase/auth.js';
import { NotificationBus } from '../services/notification-bus.js';

const isDev = import.meta.env.DEV;

// Enhanced hash-based router with authentication support
export class Router {
    constructor() {
        this.routes = {};
        this.currentPath = null;
        this.isInitialized = false;
        this.isHandlingMagicLink = false;
    }

    register(path, handler, options = {}) {
        this.routes[path] = {
            handler,
            requiresAuth: options.requiresAuth || false,
            guestOnly: options.guestOnly || false,
        };
    }

    async handleRoute() {
        const hash = window.location.hash.slice(1) || '/';

        if (isDev) {
            console.log('[ROUTER] Handling route:', hash);
        }

        // Extract path and params (support for /quote/:reference)
        const { path, params } = this.parseRoute(hash);

        const route = this.routes[path] || this.routes['/'];

        if (!route) {
            if (isDev) {
                console.warn('[ROUTER] Route not found:', hash);
            }
            return;
        }

        // Update transaction name for better grouping in Sentry
        // This groups transactions by route pattern (e.g., /quote/:reference)
        // instead of specific URLs (e.g., /#/quote/12345)
        const activeSpan = Sentry.getActiveSpan();
        if (activeSpan) {
            const rootSpan = Sentry.getRootSpan(activeSpan);
            Sentry.updateSpanName(rootSpan, path);
        }

        // Add navigation breadcrumb with route params
        Sentry.addBreadcrumb({
            type: 'navigation',
            category: 'navigation',
            message: `Navigated to ${path}`,
            level: 'info',
            data: {
                from: this.currentPath || 'initial',
                to: path,
                requiresAuth: route.requiresAuth || false,
                ...params, // Include route params (e.g., reference: '12345')
            },
        });
        this.currentPath = path;

        const { authState, getCurrentUser } = useAuth();

        // Wait for auth state to be determined
        if (authState.loading) {
            if (isDev) {
                console.log('[ROUTER] Auth state loading, waiting...');
            }
            try {
                await getCurrentUser();
            } catch (error) {
                console.error('[ROUTER] Error getting current user:', error);
            }
        }

        // Check if route is guest-only (e.g., sign-in page)
        if (route.guestOnly && authState.authenticated) {
            if (isDev) {
                console.log(
                    '[ROUTER] Guest-only route accessed by authenticated user, redirecting to home'
                );
            }
            // Redirect authenticated users to home
            this.navigate('/');
            return;
        }

        // Check if route requires authentication
        if (route.requiresAuth && !authState.authenticated) {
            if (isDev) {
                console.log('[ROUTER] Route requires auth, redirecting to sign-in');
            }
            // Save intended destination
            window.sessionStorage.setItem('authRedirect', hash);
            // Redirect to sign-in
            this.navigate('/sign-in');
            return;
        }

        // Execute route handler
        if (isDev) {
            console.log('[ROUTER] Executing route handler for:', path);
        }
        route.handler(params);
    }

    parseRoute(hash) {
        const parts = hash.split('/').filter((p) => p);

        // Check for parameterized routes
        if (parts.length === 2 && parts[0] === 'quote') {
            return {
                path: '/quote/:reference',
                params: { reference: parts[1] },
            };
        }

        // Default: exact match
        return {
            path: hash || '/',
            params: {},
        };
    }

    navigate(path) {
        window.location.hash = path;
    }

    _setupListeners() {
        // Set up auth state listener. The magic-link flow holds
        // isHandlingMagicLink for the duration of initialize() (set/cleared
        // there in a finally block) so auth-state events that fire mid-flow
        // are suppressed without a single-shot race.
        window.addEventListener('auth-state-changed', () => {
            if (this.isHandlingMagicLink) {
                if (isDev) {
                    console.log('[ROUTER] Auth state changed during magic link flow, skipping');
                }
                return;
            }

            if (isDev) {
                console.log('[ROUTER] Auth state changed, re-checking current route');
            }
            this.handleRoute();
        });

        window.addEventListener('hashchange', () => this.handleRoute());
    }

    async initialize() {
        if (this.isInitialized) return;

        const { isEmailSignInLink, signInWithEmail } = useAuth();

        if (isDev) {
            console.log('[ROUTER] Initializing router');
            console.log('[ROUTER] Current URL:', window.location.href);
        }

        // Check if this is a magic link FIRST before setting up listeners
        const isMagicLink = isEmailSignInLink(window.location.href);

        if (isMagicLink) {
            if (isDev) {
                console.log('[ROUTER] Detected magic link, attempting authentication');
            }

            // Get email from localStorage
            const email = window.localStorage.getItem('emailForSignIn');

            if (email) {
                this.isHandlingMagicLink = true;
                try {
                    // Show loading state
                    const app = document.querySelector('app-shell');
                    if (app) app.currentPage = 'loading';

                    // Complete authentication
                    await signInWithEmail(email);

                    Sentry.addBreadcrumb({
                        category: 'auth',
                        message: 'Magic link authentication successful',
                        level: 'info',
                    });

                    if (isDev) {
                        console.log('[ROUTER] Magic link authentication successful');
                    }

                    // Prefer sessionStorage (set before sign-in redirect), but fall back
                    // to the current URL hash — covers cross-tab/cross-browser magic link
                    // opens where sessionStorage from the original tab is unavailable.
                    const currentHash = window.location.hash.slice(1) || '/';
                    const redirect = window.sessionStorage.getItem('authRedirect') || currentHash;
                    window.sessionStorage.removeItem('authRedirect');

                    if (isDev) {
                        console.log('[ROUTER] Redirecting to:', redirect);
                    }

                    // Strip magic-link search params; preserve hash so navigation works.
                    window.history.replaceState(
                        null,
                        '',
                        `${window.location.pathname}${window.location.hash}`
                    );

                    this._setupListeners();
                    this.isInitialized = true;

                    if (isDev) {
                        console.log('[ROUTER] Router initialized (magic link flow)');
                    }

                    if (currentHash === redirect) {
                        if (isDev) {
                            console.log('[ROUTER] Already at destination, handling route');
                        }
                        await this.handleRoute();
                    } else {
                        this.navigate(redirect);
                        // Yield a macrotask so the hashchange-driven handleRoute
                        // completes against the new hash before isHandlingMagicLink
                        // releases. Without this yield, a late auth-state event
                        // could route against the stale /sign-in hash.
                        await new Promise((resolve) => setTimeout(resolve, 0));
                    }

                    return;
                } catch (error) {
                    console.error('[ROUTER] Magic link authentication failed:', error);

                    Sentry.captureException(error, {
                        tags: { feature: 'magic-link-auth' },
                        level: 'error',
                    });

                    NotificationBus.persistent(
                        'The sign-in link has expired or is invalid. Please request a new one.',
                        'error',
                        'router'
                    );

                    window.history.replaceState(null, '', `${window.location.pathname}#/sign-in`);
                    this._setupListeners();
                    this.navigate('/sign-in');
                    await new Promise((resolve) => setTimeout(resolve, 0));
                    this.isInitialized = true;

                    if (isDev) {
                        console.log('[ROUTER] Router initialized (magic link error flow)');
                    }

                    return;
                } finally {
                    this.isHandlingMagicLink = false;
                }
            } else {
                if (isDev) {
                    console.warn('[ROUTER] Magic link detected but no email in localStorage');
                }

                // Capture missing email scenario in Sentry
                Sentry.captureMessage('Magic link clicked without stored email', {
                    level: 'warning',
                    tags: {
                        feature: 'magic-link-auth',
                        missingContext: 'email',
                    },
                    contexts: {
                        navigation: {
                            currentHash: window.location.hash,
                            authRedirect: window.sessionStorage.getItem('authRedirect') || null,
                            referrer: document.referrer || null,
                        },
                    },
                });

                // Flag for sign-in page to show "complete sign-in" mode
                window.sessionStorage.setItem('magicLinkFailed', 'cross-browser');

                // Preserve magic link query params so sign-in page can complete auth
                window.history.replaceState(
                    null,
                    '',
                    `${window.location.pathname}${window.location.search}#/sign-in`
                );

                // Set up listeners before navigating
                this._setupListeners();

                this.navigate('/sign-in');

                // Mark as initialized
                this.isInitialized = true;

                if (isDev) {
                    console.log('[ROUTER] Router initialized (magic link no email flow)');
                }

                // Exit early
                return;
            }
        }

        // Set up listeners for non-magic-link flows
        this._setupListeners();

        // Handle initial route only if NOT a magic link
        // (magic link flow already navigates to the correct route)
        if (!isMagicLink) {
            await this.handleRoute();
        }

        this.isInitialized = true;

        if (isDev) {
            console.log('[ROUTER] Router initialized');
        }
    }
}
